import React, { useState, useEffect } from 'react';
import sortBy from 'lodash/sortBy';

const defaultStyles = {
  mainComponent: {
    width: '100%',
    display: 'flex',
    flexWrap: 'no-wrap',
    justifyContent: 'space-between',
  },
  centerDiv: {
    marginTop: '120px',
    width: '60px',
  },
  controlButton: {
    margin: '10px',
    padding: '6px 12px',
    fontWeight: 'bold',
    color: 'white',
    backgroundColor: '#2f55cc',
    border: '0',
    boxShadow: 'none',
    borderRadius: '6px',
  },
  formItem: {
    width: '100%',
    minWidth: '200px',
    padding: '6px',
  },
  select: {
    flex: 1,
  },
  unselectLink: {
    textAlign: 'right',
    cursor: 'pointer',
  },
  formItemContainer: {
    height: '300px',
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  },
};

function getJustSelectable(options) {
  return options
    .filter(o => o.value || o.options)
    .map(o => {
      if (o.value) return o;
      if (o.options) return getJustSelectable(o.options);
      return undefined;
    })
    .flat(Infinity);
}

export default function DualListBox({ ...props }) {
  const {
    id,
    onChange,
    options,
    styles,
    selected: preSelected,
    filterPlaceholder,
    selectableLabel = 'Avalible',
    removeAllLinkText = 'Remove All',
  } = props;

  useEffect(() => {
    if (!options || options.length < 0) return;
    const results = getJustSelectable(options);
    const selectedOptions =
      preSelected.map(s => results.find(o => String(o.value) === String(s.value))).filter(o => o) || [];
    const selectedIds = selectedOptions.map(o => String(o.value));

    setAllOptions(results);
    setSelectable(results.filter(o => !selectedIds.includes(String(o.value))));
    setSelected(selectedOptions);
  }, [options, preSelected]);

  const [allOptions, setAllOptions] = useState([]);
  const [selected, setSelected] = useState([]);
  const [selectable, setSelectable] = useState([]);
  const [stageSelect, setStageSelect] = useState([]);
  const [stageUnselect, setStageUnselect] = useState([]);
  const updatedStyles = styles ? { ...defaultStyles, ...styles } : { ...defaultStyles };

  function addToSelected() {
    const newSelected = [...selected, ...stageSelect];
    setSelected(newSelected);
    setSelectable([...selectable.filter(s => !stageSelect.find(ss => String(ss.value) === String(s.value)))]);
    setStageSelect([]);
    //emulating an event or just send values as a second arg
    onChange({ target: { value: newSelected }, id }, newSelected);
  }

  function removeFromSelected() {
    const newSelected = [...selected.filter(s => !stageUnselect.find(su => String(su.value) === String(s.value)))];
    setSelected(newSelected);
    setSelectable([...selectable, ...stageUnselect]);
    setStageUnselect([]);
    //emulating an event or just send values as a second arg
    onChange({ target: { value: newSelected }, id }, newSelected);
  }

  function unselectAll() {
    setSelected([]);
    setSelectable([...selectable, ...selected]);
    setStageUnselect([]);
    //emulating an event or just send values as a second arg
    onChange({ target: { value: [] }, id }, []);
  }

  function getSelection(target) {
    const selectField = target.options
      ? target
      : target.parentNode.tagName === 'SELECT'
        ? target.parentNode
        : target.parentNode.parentNode;

    return Array.from(selectField.options)
      .filter(o => o.selected)
      .map(o => ({ value: String(o.value), label: o.label }));
  }

  function handleFilter({ target: { value } }) {
    //if no value send everything
    const avalibleOptions = allOptions
      //TODO Apply regex instead here
      .filter(o => !selected.find(s => String(s.value) === String(o.value)))
      .map(o => ({ ...o, value: String(o.value) }));

    if (!value.length) return setSelectable(avalibleOptions);
    const search = value.toLowerCase();
    //This can do a full search on any item in the object
    const keys = Object.keys(avalibleOptions[0]);
    setSelectable(
      avalibleOptions.filter(o => {
        for (let key of keys) {
          if (String(o[key]).toLowerCase().includes(search)) return true;
        }
        return false;
      })
    );
  }

  function anOption(item, validOptions, idx) {
    if (validOptions.find(vO => String(vO.value) === String(item.value)))
      return (
        <option key={idx} value={item.value}>
          {item.label}
        </option>
      );
    return;
  }

  function getChildrenOptions(item, validOptions, label) {
    return (
      sortBy(item.options, ['label'])
        //Since html does not allow nested optgroups, this makes the labels recursively
        //loop and add each parent label to the one before 'parent a / parent b / parent c
        //as the label for an optgroup.
        .map((option, idx) =>
          createOptionsList(option, idx, label ? `${label} / ${item.label}` : `${item.label}`, validOptions)
        )
        .filter(option => option)
    );
  }

  function createOptionsList(item, idx, label, validOptions) {
    //If the object has a value property it is an option
    if (item.value) return anOption(item, validOptions, idx);

    const nestedOptions = getChildrenOptions(item, validOptions, label);

    //if the object has an options property, it is a parent group
    if (item.options.find(c => c.options)) return nestedOptions;

    //if the parent group has no children, don't show it.
    if (nestedOptions.length <= 0) return;

    return (
      <optgroup key={idx} label={`${label} / ${item.label}`}>
        {nestedOptions}
      </optgroup>
    );
  }

  return (
    <div style={updatedStyles.mainComponent}>
      <div style={updatedStyles.formItemContainer}>
        <label>{selectableLabel}</label>
        <input
          id="filter"
          name="filter"
          placeholder={filterPlaceholder}
          style={updatedStyles.formItem}
          onChange={handleFilter}
        />
        <select
          name="selectable"
          id="selectable"
          multiple={true}
          style={{ ...updatedStyles.formItem, ...updatedStyles.select }}
          onClick={({ target }) => setStageSelect(getSelection(target))}
          selected={stageSelect}>
          {sortBy(options, ['label']).map((s, idx) => createOptionsList(s, idx, null, selectable))}
        </select>
      </div>
      <div style={updatedStyles.centerDiv}>
        <button style={updatedStyles.controlButton} onClick={addToSelected}>
          {`>`}
        </button>
        <button style={updatedStyles.controlButton} onClick={removeFromSelected}>
          {`<`}
        </button>
      </div>
      <div style={updatedStyles.formItemContainer}>
        <div style={updatedStyles.unselectLink}>
          <a onClick={unselectAll}>{removeAllLinkText}</a>
        </div>
        <select
          name="selected"
          id="selected"
          multiple={true}
          style={{ ...updatedStyles.formItem, ...updatedStyles.select }}
          onClick={({ target }) => setStageUnselect(getSelection(target))}
          selected={stageUnselect}>
          {sortBy(options, ['label']).map((s, idx) => createOptionsList(s, idx, null, selected))}
        </select>
      </div>
    </div>
  );
}
